import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import navItems, { hiddenRoutes } from '../../Utils/NavItems';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const flattenedRoutes = React.useMemo(() => {
    const flat = [...hiddenRoutes];
    navItems.forEach((item) => {
      flat.push(item);
      if (item.children?.length) {
        flat.push(...item.children);
      }
    });
    return flat;
  }, []);

  const toReadableLabel = (value: string): string => {
    return value
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const matchesDynamicRoute = (routePath: string, currentPath: string): boolean => {
    const routeParts = routePath.split('/').filter(Boolean);
    const currentParts = currentPath.split('/').filter(Boolean);

    if (routeParts.length !== currentParts.length) {
      return false;
    }

    return routeParts.every((part, index) => part.startsWith(':') || part === currentParts[index]);
  };

  const resolveLabel = (currentPath: string, currentSegment: string): { label: string | null; matched: boolean } => {
    const exact = flattenedRoutes.find((item) => item.path === currentPath);
    if (exact) {
      return { label: exact.title, matched: true };
    }

    const dynamic = flattenedRoutes.find((item) => item.path.includes(':') && matchesDynamicRoute(item.path, currentPath));
    if (dynamic) {
      return { label: dynamic.title, matched: true };
    }

    return { label: toReadableLabel(currentSegment), matched: false };
  };

  const breadcrumbItems: BreadcrumbItem[] = [];
  for (let index = 0; index < pathnames.length; index += 1) {
    const segment = pathnames[index];
    const to = `/${pathnames.slice(0, index + 1).join('/')}`;
    const nextPath = index < pathnames.length - 1
      ? `/${pathnames.slice(0, index + 2).join('/')}`
      : null;

    const current = resolveLabel(to, segment);
    const nextHasDynamicMatch = Boolean(
      !current.matched
      && nextPath
      && flattenedRoutes.some((item) => item.path.includes(':') && matchesDynamicRoute(item.path, nextPath))
    );

    // Skip noisy intermediate segments when the next segment maps to a dynamic titled route.
    if (nextHasDynamicMatch) {
      continue;
    }

    breadcrumbItems.push({ label: current.label || toReadableLabel(segment), path: to });
  }

  // Don't show breadcrumbs on home page
  if (pathnames.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        py: 2,
        px: { xs: 2, sm: 3 },
        backgroundColor: '#fff',
        borderBottom: '1px solid #e5e7eb',
        borderRadius: { xs: 0, sm: '8px' },
        mb: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#9ca3af' }} />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
          },
        }}
      >
        {/* Home link */}
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#6b7280',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'color 0.2s',
            '&:hover': {
              color: '#1e3c72',
            },
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
          Home
        </Link>

        {/* Dynamic breadcrumb items */}
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return isLast ? (
            <Typography
              key={item.path}
              sx={{
                color: '#1e3c72',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {item.label}
            </Typography>
          ) : (
            <Link
              key={item.path}
              component={RouterLink}
              to={item.path}
              underline="hover"
              sx={{
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: 500,
                transition: 'color 0.2s',
                '&:hover': {
                  color: '#1e3c72',
                },
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
